import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IForgotPasswordRequest, IResetPasswordRequest, IResetPasswordResponse, IUser, IUserLoginRequest, IUserLoginResponse } from '../../../domain/interfaces/user/interface-user';
import { AlertType } from '../../../domain/models/alert/alert-type-enum';
import { AuthGateway } from '../../../domain/models/users/gateway/auth-gateway';
import { environment } from '../../../../environments/environment';
import { AlertService } from '../alert-service/alert.service';
import { TokenService } from './token.service';
import { CypherService } from '../cypher-service/cypher.service';
import { INotification } from 'src/app/domain/interfaces/notification/notification';
import { IEverflowData } from 'src/app/domain/interfaces/user/interface-user-everflow-data';
import { RewardService } from '../reward-service/reward.service';
import { financeify } from 'src/assets/data/urls-for-brand';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends AuthGateway {
  private UserLogin!: BehaviorSubject<IUser>;
  private Notification!: BehaviorSubject<INotification>;
  private EverflowData!: BehaviorSubject<IEverflowData>;
  public UserLoginResponse!: IUserLoginResponse;
  private refreshTokenTimeout!: ReturnType<typeof setTimeout>;
  public PublicIPAddress: string = "";
  private currentLoginURLForBrand:string;


  constructor(private router: Router, private http: HttpClient, private tokenService: TokenService,
    private alertService: AlertService, private cypherService: CypherService, private rewardService: RewardService) {
    super();
    let currentTokenStorage = this.tokenService.getToken();
    let currentUserStorage = this.tokenService.getUser();
    let currentRTokenStorage = this.tokenService.getRefreshToken();
    let currentNotificationStorage = this.tokenService.getNotification();
    let currentEverflowData = this.tokenService.getUserEverflowData();
    this.UserLoginResponse = {
      data: {
        token: currentTokenStorage!,
        refreshToken: currentRTokenStorage!,
        user: currentUserStorage!,
        everflowEvent: currentEverflowData!
      }
    }
    this.UserLogin = new BehaviorSubject<IUser>(currentUserStorage!);
    this.Notification = new BehaviorSubject<INotification>(currentNotificationStorage!);
    this.EverflowData = new BehaviorSubject<IEverflowData>(currentEverflowData!);
    this.currentLoginURLForBrand = "/login";
  }

  public Login(user: IUserLoginRequest, loginUrlForBrand: string): Observable<IUserLoginResponse> {
    let encryptedPassword = this.cypherService.EncryptText(user.password);
    return this.http.post<any>(`${environment.apiUrl}/api/user/authenticate`, { Email: user.email?.toLowerCase(), Password: encryptedPassword })
      .pipe(map((UserLoginResponse: IUserLoginResponse) => {
        if(UserLoginResponse.data?.user?.isNewMemberPortal) {
          const info = btoa(UserLoginResponse.data?.user?.email) 
          if(!environment.production) {
            window.location.href = `${loginUrlForBrand.replace("://","://app.").replace("auth/","").replace("-v2","")}?redirect=1&data=${info}`
          } else {
            window.location.href = `${loginUrlForBrand.replace("members", "app")}/login?redirect=1&data=${info}`  
          }
          return UserLoginResponse;
        }
        this.UserLoginResponse = UserLoginResponse;
        this.UserLoginResponse!.data!.user!.getGeoCreditScore = true;
        this.UserLogin?.next(this.UserLoginResponse?.data?.user!);
        this.EverflowData?.next(this.UserLoginResponse?.data?.everflowEvent!);
        this.tokenService.saveUser(this.UserLoginResponse?.data?.user!);
        this.tokenService.saveToken(this.UserLoginResponse?.data?.token!);
        this.tokenService.saveRefreshToken(this.UserLoginResponse?.data?.refreshToken!);
        this.tokenService.saveNotification(this.UserLoginResponse?.data?.notification!);
        this.Notification?.next(this.UserLoginResponse?.data?.notification!);
        if(UserLoginResponse.data?.user?.activeReward) {
          this.rewardService.setTokenReward(UserLoginResponse.data!.tokenRewardProgram!);
        }
        return UserLoginResponse;
      }));
  }

  public GetIPAddress(): any {
    return this.http.get("https://api.ipify.org/?format=json");
  }

  public GetUserLogin(): Observable<IUser> {
    return this.UserLogin?.asObservable().pipe();
  }

  public GetUserEverflowDataLogin(): Observable<IEverflowData> {
    return this.EverflowData?.asObservable().pipe();
  }

  public SaveUserLogin(User: IUser): void {
    this.UserLogin?.next(User!);
    this.tokenService.saveUser(User);
  }

  Logout(): void {
    this.alertService.hideAlert();
    this.tokenService.clearStorage();
    this.UserLogin?.next(null!);
    this.Notification?.next(null!);
    this.UserLoginResponse = {};
    this.stopRefreshTokenTimer();
    this.router.navigate([this.currentLoginURLForBrand]);
  }

  refreshToken() {
    var refreshToken = this.tokenService.getRefreshToken();
    const headers = new HttpHeaders().append('refresh-token', `${refreshToken}`);
    return this.http.post<any>(`${environment.apiUrl}/api/user/refreshtoken`, null, { headers: headers })
      .subscribe((UserLoginResponse) => {
        this.UserLoginResponse = UserLoginResponse;
        this.UserLogin?.next(this.UserLoginResponse?.data?.user!);
        this.EverflowData?.next(this.UserLoginResponse?.data?.everflowEvent!);
        this.tokenService.saveToken(this.UserLoginResponse?.data?.token!);
        this.tokenService.saveUser(this.UserLoginResponse?.data?.user!);
        this.tokenService.saveRefreshToken(this.UserLoginResponse?.data?.refreshToken!);
        this.startRefreshTokenTimer();
        return UserLoginResponse;
      });
  }

  forgotPassword(forgotPassword: IForgotPasswordRequest): Observable<IResetPasswordResponse> {
    forgotPassword.email = forgotPassword?.email?.toLowerCase();
    return this.http.post<IResetPasswordResponse>(`${environment.apiUrl}/api/user/forgotpassword`, forgotPassword)
      .pipe(map((resetPasswordResponse: IResetPasswordResponse) => {
        return resetPasswordResponse!;
      }));
  }

  validatePasswordResetToken(resetPassword: IResetPasswordRequest): Observable<IResetPasswordResponse> {
    return this.http.post<IResetPasswordResponse>(`${environment.apiUrl}/api/user/validateresettoken`, resetPassword)
      .pipe(map((resetPasswordResponse: IResetPasswordResponse) => {
        return resetPasswordResponse!;
      }));
  }

  resetPassword(resetPassword: IResetPasswordRequest): Observable<IResetPasswordResponse> {
    resetPassword.password = this.cypherService.EncryptText(resetPassword.password!);
    return this.http.post<IResetPasswordResponse>(`${environment.apiUrl}/api/user/resetpassword`, resetPassword)
      .pipe(map((resetPasswordResponse: IResetPasswordResponse) => {
        return resetPasswordResponse!;
      }));
  }

  getLastActivity(): string {
    let activity: string = sessionStorage.getItem("last-activity-date")!;
    if (activity) {
      return this.cypherService.DecryptText(activity);
    }
    return "";
  }

  setLastActivity() {
    sessionStorage.setItem("last-activity-date", this.cypherService.EncryptText(new Date().toUTCString()));
  }

  validateDownTime() {
    let lastActivity = new Date(this.getLastActivity());
    const lastActivityUTC = new Date(new Date(lastActivity).getUTCFullYear(),
      new Date(lastActivity).getUTCMonth(), new Date(lastActivity).getUTCDate(),
      new Date(lastActivity).getUTCHours(), (new Date(lastActivity).getUTCMinutes()),
      new Date().getUTCSeconds());

    const inactivityDeadlineUTC = new Date(
      new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(),
      new Date().getUTCHours(), (new Date().getUTCMinutes() - environment.maxSessionIdleTimeMinutes),
      new Date().getUTCSeconds());

    if (lastActivityUTC < inactivityDeadlineUTC) {
      this.alertService.showAlert({
        Type: AlertType.InfoLogo,
        Description: "Max idle time reached.",
        ShowAsModal: true,
        AutoClose: false,
        BtnText: "Login",
        BtnLink: this.currentLoginURLForBrand,
      });
      this.Logout();
    } else {
      this.refreshToken();
    }
  }

  public startRefreshTokenTimer() {
    // parse json object from base64 encoded jwt token
    const jwtToken = JSON.parse(atob(this.UserLoginResponse!.data!.token!.split('.')[1]));
    let tokenExpiration = new Date(jwtToken.exp * 1000);
    // set a timeout to refresh the token a minute before it expires
    const timeout = tokenExpiration.getTime() - Date.now() - (60 * 1000);
    //Validate refresh token only if the JWT token has not expired:
    if (timeout > 0) {
      this.refreshTokenTimeout = setTimeout(() => this.validateDownTime(), timeout);
    }
    else {
      this.Logout();
    }
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  public ShowToolTips() {
    this.UserLoginResponse.data!.user!.showDashboardToolTip = true;
    this.SaveUserLogin(this.UserLoginResponse.data!.user!);
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    }
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate(['/member-portal/dashboard']);
  }

  public SaveNotification(Notification: INotification): void {
    this.Notification?.next(Notification!);
    this.tokenService.saveNotification(Notification);
  }

  public GetNotification(): Observable<INotification> {
    return this.Notification?.asObservable().pipe();
  }
}